import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Component, ComponentRef, HostListener, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { map, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { CanComponentDeactivate } from "../../../../security/unsaved-changes.guard";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { DropDownModel } from "../../../../domain/model/common/dropDownModel";
import { EnrollmentEntityRentalPropertyLicense } from "../../../../domain/model/enrollment/enrollmentEntityRentalPropertyLicense";
import { EnrollmentRentalProperty } from "../../../../domain/model/enrollment/enrollmentRentalProperty";
import { EnrollmentRentalPropertyLicense } from "../../../../domain/model/enrollment/enrollmentRentalPropertyLicense";
import { EnrollmentTaxRepresentative } from "../../../../domain/model/enrollment/enrollmentTaxRepresentative";
import { HotelMotelEnrollment } from "../../../../domain/model/enrollment/hotelMotelEnrollment";
import { NotificationType } from "../../../../domain/model/enums/notificationType";
import { CommonServices } from "../../../../domain/service/commonService";
import { EnrollmentService } from "../../../../domain/service/enrollmentService";
import { GooglePlacesService } from "../../../../domain/service/GooglePlaceService";
import { NotificationComponent } from "../../../../shared/notification/notification.component";
import { atLeastOneRequiredValidator } from "../../../../shared/utils/atLeastOneRequiredValidator";
import { DialogConfirmComponent } from "../../dialog-confirm/dialog-confirm.component";
import { Router } from "@angular/router";
import { EntitylicensestepComponent } from "../shared/entitylicensestep/entitylicensestep.component";
import { RentalPropertyLicenseComponent } from "../shared/rental-property-license/rental-property-license.component";
import { RentalPropertyReportingFormComponent } from "../shared/rental-property-reporting-form/rental-property-reporting-form.component";
import { RentalPropertyComponent } from "../shared/rental-property/rental-property.component";

@Component({
  selector: "app-add-hotel-motel",
  templateUrl: "./add-hotel-motel.component.html",
  styleUrl: "./add-hotel-motel.component.css",
})
export class AddHotelMotelComponent implements CanComponentDeactivate {
  @ViewChild("entitylicensestep") entitylicensestep: EntitylicensestepComponent | undefined;
  @ViewChild("rentalPropertyLicenseComponent") rentalPropertyLicenseComponent:
    | RentalPropertyLicenseComponent
    | undefined;
  @ViewChild("rentalPropertyComponent") rentalPropertyComponent: RentalPropertyComponent | undefined;
  @ViewChild("rentalPropertyReportingFormComponent") rentalPropertyReportingFormComponent:
    | RentalPropertyReportingFormComponent
    | undefined;
  private overlayRef!: OverlayRef;
  suggestions: any[] = [];
  constructor(
    private googlePlacesService: GooglePlacesService,
    private overlay: Overlay,
    public dialogRef: MatDialog,
    private commonServices: CommonServices,
    private _formBuilder: FormBuilder,
    private enrollment: EnrollmentService,
    private router: Router
  ) {
    this.hotelForm.get("ownerStreetAddress")?.valueChanges.subscribe((value) => {
      if (value) {
        this.googlePlacesService.getPlaces(value).then((predictions: any) => {
          this.suggestions = predictions;
          console.log(this.suggestions);
        });
      } else {
        this.suggestions = [];
      }
    });
  }
  ngOnInit(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.overlay.position().global().top("20px").right("30px"),
    });
    this.showMessageRentalProperty();

    this.commonServices.getAllState(233).subscribe((data) => {
      this.states = data.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.commonServices.getAllCountry().subscribe((data) => {
      this.country = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.wireZipAutofill(this.hotelForm, true);
    this.wireZipAutofill(this.rentalProperty);
  }

  /**
   * When a 5-digit zip is entered, look up city/state and fill them in.
   * Uses { emitEvent: false } so filling fields doesn't re-trigger lookups.
   */
  /** stateAsString: hotelForm's State options are `id.toString()` (see add-individual-owner). */
  private wireZipAutofill(form: FormGroup, stateAsString = false): void {
    form
      .get("zipCode")
      ?.valueChanges.pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(async (zip: string | null) => {
        const loc = await this.googlePlacesService.getLocationByZip(zip ?? "");
        if (!loc) {
          return;
        }
        if (loc.city) {
          form.get("city")?.setValue(loc.city, { emitEvent: false });
        }
        if (loc.stateCode) {
          const stateId = this.states.find((e) => e.iso2 == loc.stateCode)?.id;
          if (stateId) {
            form.get("state")?.setValue(stateAsString ? stateId.toString() : stateId, { emitEvent: false });
          }
        }
      });
  }

  showNotification(message: string, title: string, notificationType: NotificationType): void {
    // Create a Portal instance of our `NotificationComponent`
    const notificationPortal = new ComponentPortal(NotificationComponent);
    // Attach our `NotificationComponent` to the Overlay Portal
    const notificationRef: ComponentRef<NotificationComponent> = this.overlayRef.attach(notificationPortal);
    // Now we can access the component instance
    // and we can pass input for the `message` prop we created.
    notificationRef.instance.message = message;
    notificationRef.instance.title = title;
    notificationRef.instance.type = notificationType;
    notificationRef.instance.overlayRef = this.overlayRef;
    notificationRef.instance.getColor();

    // Set a timeout to detach our component from the overlay
    // after 3000 milliseconds
    setTimeout(() => {
      this.overlayRef.detach();
    }, 10000);
  }
  entityRentalPropertyLicense = new FormGroup(
    {
      departmentOfBusiness: new FormControl("", [Validators.pattern(/^[a-zA-Z1-9][a-zA-Z0-9]*$/)]),
      taxCertificateNumber: new FormControl(""),
      stateCertificateHasBeenApplied: new FormControl(null, [Validators.requiredTrue]),
      reasonWhy: new FormControl("", [Validators.required]),
      certificateOfRegistration: new FormControl(""),
      localBusinessTaxReceipt: new FormControl(""),
      cityOfRegistration: new FormControl(""),
      primaryContactFirstName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
      primaryContactLastName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
      primaryContactEmail: new FormControl("", [Validators.required, Validators.maxLength(320), Validators.email]),
      primaryContactPhoneNumber: new FormControl("", [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/),
      ]),
    },
    { validators: atLeastOneRequiredValidator(["reasonWhy", "stateCertificateHasBeenApplied", "taxCertificateNumber"]) }
  );
  rentalPropertyLicense = new FormGroup(
    {
      departmentOfBusiness: new FormControl("", [Validators.pattern(/^[a-zA-Z1-9][a-zA-Z0-9]*$/)]),
      taxCertificateNumber: new FormControl(""),
      stateCertificateHasBeenApplied: new FormControl(null),
      reasonWhy: new FormControl(""),
      certificateOfRegistration: new FormControl(""),
      localBusinessTaxReceipt: new FormControl(""),
      cityOfRegistration: new FormControl(""),
    },
    { validators: atLeastOneRequiredValidator(["reasonWhy", "stateCertificateHasBeenApplied", "taxCertificateNumber"]) }
  );

  rentalProperty = new FormGroup({
    propertyStreetAddress: new FormControl("", [Validators.required]),
    aptUnitSuite: new FormControl(""),
    city: new FormControl("", [Validators.required]),
    state: new FormControl(1436, [Validators.required]),
    zipCode: new FormControl("", [
      Validators.required,
      Validators.maxLength(400),
      Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
    ]),
    startedRenting: new FormControl(null, [Validators.required]),
    filingFrequency: new FormControl("", [Validators.required]),
    businessName: new FormControl(""),
    webUrl: new FormControl("", [
      Validators.pattern(/^(https?:\/\/|www\.)[a-zA-Z0-9\-\.]+(?:\.[a-zA-Z]{2,})(?:\/[a-zA-Z0-9\-]+)*\/?$/),
    ]),
    usePlatformToRent: new FormControl(""),
    platFormLink: new FormControl(""),
    rentalPropertyBusiness: new FormControl(""),
  });
  addressType: string = "1";
  states: DropDownModel[] = [];
  country: DropDownModel[] = [];
  showCityLocalRequirements = false;
  showTaxRepresentativeForm: boolean = false;
  stepCompleted: boolean = false;
  hotelForm = new FormGroup({
    firstName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
    lastName: new FormControl("", [Validators.required, Validators.maxLength(200)]),
    email: new FormControl("", [Validators.required, Validators.maxLength(320), Validators.email]),
    phoneNumber: new FormControl("", [
      Validators.required,
      Validators.maxLength(100),
      Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/),
    ]),
    note: new FormControl(""),
    ownerStreetAddress: new FormControl("", Validators.required),
    aptUnitSuite: new FormControl(""),
    city: new FormControl("", Validators.required),
    state: new FormControl("", Validators.required),
    zipCode: new FormControl("", [
      Validators.required,
      Validators.maxLength(12),
      Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
    ]),
    addressName: new FormControl(""),
    addressOrPoBox: new FormControl(""),
    cityStateProvince: new FormControl(""),
    country: new FormControl(""),
  });

  reporting = new FormGroup({
    isUsingTaxRepresentative: new FormControl(null),
    sendToOwner: new FormControl(false),
    sendToReportingRep: new FormControl(),
    taxRepresentativeFirstName: new FormControl(),
    taxRepresentativeLastName: new FormControl(),
    taxRepresentativeEmail: new FormControl(),
    taxRepresentativePhoneNumber: new FormControl(),
    taxRepresentativeStreetAddress: new FormControl(),
    taxRepresentativeAptUnitSuite: new FormControl(),
    taxRepresentativeCity: new FormControl(),
    taxRepresentativeState: new FormControl(),
    taxRepresentativeZipCode: new FormControl(),
  });

  radioChangeAddress(event: any) {
    this.addressType = event.value;
    if (event.value == "1") {
      this.hotelForm.get("addressName")?.clearValidators();
      this.hotelForm.get("addressName")?.updateValueAndValidity();

      this.hotelForm.get("addressOrPoBox")?.clearValidators();
      this.hotelForm.get("addressOrPoBox")?.updateValueAndValidity();

      this.hotelForm.get("cityStateProvince")?.clearValidators();
      this.hotelForm.get("cityStateProvince")?.updateValueAndValidity();

      this.hotelForm.get("country")?.clearValidators();
      this.hotelForm.get("country")?.updateValueAndValidity();

      this.hotelForm.get("ownerStreetAddress")?.addValidators(Validators.required);
      this.hotelForm.get("ownerStreetAddress")?.updateValueAndValidity();

      this.hotelForm.get("aptUnitSuite")?.addValidators(Validators.required);
      this.hotelForm.get("aptUnitSuite")?.updateValueAndValidity();

      this.hotelForm.get("city")?.addValidators(Validators.required);
      this.hotelForm.get("city")?.updateValueAndValidity();

      this.hotelForm.get("state")?.addValidators(Validators.required);
      this.hotelForm.get("state")?.updateValueAndValidity();

      this.hotelForm.get("zipCode")?.addValidators(Validators.required);
      this.hotelForm.get("zipCode")?.addValidators(Validators.maxLength(12));
      this.hotelForm.get("zipCode")?.addValidators(Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/));
      this.hotelForm.get("zipCode")?.updateValueAndValidity();
    }
    if (event.value == "2") {
      this.hotelForm.get("addressName")?.addValidators(Validators.required);
      this.hotelForm.get("addressName")?.updateValueAndValidity();

      this.hotelForm.get("addressOrPoBox")?.addValidators(Validators.required);
      this.hotelForm.get("addressOrPoBox")?.updateValueAndValidity();

      this.hotelForm.get("cityStateProvince")?.addValidators(Validators.required);
      this.hotelForm.get("cityStateProvince")?.updateValueAndValidity();

      this.hotelForm.get("country")?.addValidators(Validators.required);
      this.hotelForm.get("country")?.updateValueAndValidity();

      this.hotelForm.get("ownerStreetAddress")?.clearValidators();
      this.hotelForm.get("ownerStreetAddress")?.updateValueAndValidity();

      this.hotelForm.get("aptUnitSuite")?.clearValidators();
      this.hotelForm.get("aptUnitSuite")?.updateValueAndValidity();

      this.hotelForm.get("city")?.clearValidators();
      this.hotelForm.get("city")?.updateValueAndValidity();

      this.hotelForm.get("state")?.clearValidators();
      this.hotelForm.get("state")?.updateValueAndValidity();

      this.hotelForm.get("zipCode")?.clearValidators();
      this.hotelForm.get("zipCode")?.updateValueAndValidity();
    }
  }
  agreements = new FormGroup({
    confirmRead: new FormControl(null, [Validators.requiredTrue]),
    confirmAllInformation: new FormControl(null, [Validators.requiredTrue]),
    printedName: new FormControl("", [Validators.required]),
  });
  goBack(stepper: MatStepper) {
    stepper.previous();
    if (stepper.selectedIndex == 2) {
      this.showMessageRentalProperty();
    }
  }
  goForward(stepper: MatStepper) {
    stepper.next();

    if (stepper.selectedIndex == 2) {
      this.showMessageRentalProperty();
    }
  }
  showMessageRentalProperty() {
    this.showNotification(
      "During the registration process you will be required to add at least one property to complete your account setup. Once your account is verified, you will have the flexibility to add as many properties as you wish within our platform.",
      "Important",
      NotificationType.InfoStepper
    );
  }
  showAndHideTaxForm(show: boolean) {
    this.showTaxRepresentativeForm = show;
    if (show) {
      this.reporting.get("taxRepresentativeFirstName")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeFirstName")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeLastName")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeLastName")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeEmail")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeEmail")?.addValidators(Validators.email);
      this.reporting.get("taxRepresentativeEmail")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativePhoneNumber")?.addValidators(Validators.required);
      this.reporting
        .get("taxRepresentativePhoneNumber")
        ?.addValidators(
          Validators.pattern(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/)
        );
      this.reporting.get("taxRepresentativePhoneNumber")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeStreetAddress")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeStreetAddress")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeCity")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeCity")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeState")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeState")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeZipCode")?.addValidators(Validators.required);
      this.reporting.get("taxRepresentativeZipCode")?.addValidators(Validators.maxLength(12));
      this.reporting.get("taxRepresentativeZipCode")?.addValidators(Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/));
      this.reporting.get("taxRepresentativeZipCode")?.updateValueAndValidity();
    } else {
      this.reporting.get("taxRepresentativeFirstName")?.clearValidators();
      this.reporting.get("taxRepresentativeFirstName")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeLastName")?.clearValidators();
      this.reporting.get("taxRepresentativeLastName")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeEmail")?.clearValidators();
      this.reporting.get("taxRepresentativeEmail")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativePhoneNumber")?.clearValidators();
      this.reporting.get("taxRepresentativePhoneNumber")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeStreetAddress")?.clearValidators();
      this.reporting.get("taxRepresentativeStreetAddress")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeAptUnitSuite")?.clearValidators();
      this.reporting.get("taxRepresentativeAptUnitSuite")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeCity")?.clearValidators();
      this.reporting.get("taxRepresentativeCity")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeState")?.clearValidators();
      this.reporting.get("taxRepresentativeState")?.updateValueAndValidity();

      this.reporting.get("taxRepresentativeZipCode")?.clearValidators();
      this.reporting.get("taxRepresentativeZipCode")?.updateValueAndValidity();
    }
  }
  save() {
    let enrollmentRentalPropertyLicense: EnrollmentRentalPropertyLicense = {
      regulationLicenseNumber: this.rentalPropertyLicense?.get("departmentOfBusiness")?.value ?? "",
      certificateOfRegistration: this.rentalPropertyLicense?.get("cityOfRegistration")?.value ?? "",
      taxCertificateNumber: this.rentalPropertyLicense?.get("taxCertificateNumber")?.value ?? "",
      stateCertificateHasBeenApplied: this.rentalPropertyLicense?.get("stateCertificateHasBeenApplied")?.value ?? false,
      taxReceipt: this.rentalPropertyLicense?.get("localBusinessTaxReceipt")?.value ?? "",
      reasonWhy: this.rentalPropertyLicense?.get("reasonWhy")?.value ?? "",
    };

    let enrollmentEntityRentalPropertyLicense: EnrollmentEntityRentalPropertyLicense = {
      regulationLicenseNumber: this.entityRentalPropertyLicense?.get("departmentOfBusiness")?.value ?? "",
      certicateOfRegistration: this.entityRentalPropertyLicense?.get("cityOfRegistration")?.value ?? "",
      taxCertificateNumber: this.entityRentalPropertyLicense?.get("taxCertificateNumber")?.value ?? "",
      stateCertificateHasBeenApplied:
        this.entityRentalPropertyLicense?.get("stateCertificateHasBeenApplied")?.value ?? false,
      taxReceipt: this.entityRentalPropertyLicense?.get("localBusinessTaxReceipt")?.value ?? "",
      reasonWhy: this.entityRentalPropertyLicense?.get("reasonWhy")?.value ?? "",
      primaryContactFirstName: this.entityRentalPropertyLicense?.get("primaryContactFirstName")?.value ?? "",
      primaryContactLastName: this.entityRentalPropertyLicense?.get("primaryContactLastName")?.value ?? "",
      primaryContactEmail: this.entityRentalPropertyLicense?.get("primaryContactEmail")?.value ?? "",
      primaryContactPhoneNumber: this.entityRentalPropertyLicense?.get("primaryContactPhoneNumber")?.value ?? "",
    };

    let enrollmentRentalProperty: EnrollmentRentalProperty = {
      rentalPropertyStreetAddress: this.rentalProperty?.get("propertyStreetAddress")?.value ?? "",
      aptUnitSute: this.rentalProperty?.get("aptUnitSuite")?.value ?? "",
      city: this.rentalProperty?.get("city")?.value ?? "",
      state: this.rentalProperty.get("state")?.value ?? 0,
      zipCode: this.rentalProperty?.get("zipCode")?.value ?? "",
      startedRenting: this.rentalProperty?.get("startedRenting")?.value ?? "",
      filingFrecuency: this.rentalProperty?.get("filingFrequency")?.value ?? "",
      businessName: this.rentalProperty?.get("businessName")?.value ?? "",
      webUrl: this.rentalProperty?.get("webUrl")?.value ?? "",
      useDigitalPlatform: false,
      digitalPlatform: "",
      frequency: this.rentalProperty?.get("filingFrequency")?.value ?? "",
      isOwner: false,
      isPropertyManagementCompany: false,
      // Without a tax representative the checkboxes are hidden: correspondence goes to the owner (QA 2026-10-05).
      correspondenceToOwner: (this.reporting.get("isUsingTaxRepresentative")?.value as boolean | null) !== true
        || (this.reporting.get("sendToOwner")?.value ?? false),
      correspondenceToReportingRepresentative: this.reporting.get("sendToReportingRep")?.value ?? false,
    };

    let enrollmentTaxRepresentative: EnrollmentTaxRepresentative = {
      firstName: this.reporting.get("taxRepresentativeFirstName")?.value ?? "",
      lastName: this.reporting.get("taxRepresentativeLastName")?.value ?? "",
      email: this.reporting.get("taxRepresentativeEmail")?.value ?? "",
      phoneNumber: this.reporting.get("taxRepresentativePhoneNumber")?.value ?? "",
      streetAddress: this.reporting.get("taxRepresentativeStreetAddress")?.value ?? "",
      aptUnitSuite: this.reporting.get("taxRepresentativeAptUnitSuite")?.value ?? "",
      city: this.reporting.get("taxRepresentativeCity")?.value ?? "",
      state: this.reporting.get("taxRepresentativeState")?.value
        ? Number.parseInt(this.reporting.get("taxRepresentativeState")?.value ?? "0") ?? 0
        : 0,
      zipCode: this.reporting.get("taxRepresentativeZipCode")?.value ?? "",
    };
    let model: HotelMotelEnrollment = {
      id: 0,
      printedName: this.agreements.get("printedName")?.value ?? "",
      primaryContactFirstName: this.hotelForm.get("firstName")?.value ?? "",
      primaryContactLastName: this.hotelForm.get("lastName")?.value ?? "",
      email: this.hotelForm.get("email")?.value ?? "",
      phone: this.hotelForm.get("phoneNumber")?.value ?? "",
      streetAddress: this.hotelForm.get("ownerStreetAddress")?.value ?? "",
      aptUnitSuite: this.hotelForm.get("aptUnitSuite")?.value ?? "",
      city: this.hotelForm.get("city")?.value ?? "",
      zipCode: this.hotelForm.get("zipCode")?.value ?? "",
      internationalAddressName: this.hotelForm.get("addressName")?.value ?? "",
      streetAddressPoBoxNumber: this.hotelForm.get("addressOrPoBox")?.value ?? "",
      internationalCityStateProvince: this.hotelForm.get("cityStateProvince")?.value ?? "",

      note: this.hotelForm.get("note")?.value ?? "",
      stateId: Number(this.hotelForm.get("state")?.value ?? "0") ?? 0,
      countryId: Number.parseInt(this.hotelForm.get("country")?.value ?? "0"),
      enrollmentRentalPropertyLicense: enrollmentRentalPropertyLicense,
      enrollmentEntityRentalPropertyLicense: enrollmentEntityRentalPropertyLicense,
      enrollmentRentalProperty: enrollmentRentalProperty,
      enrollmentTaxRepresentative: enrollmentTaxRepresentative,
      sendToOwner: this.reporting.get("sendToOwner")?.value ?? false,
      sendToTaxReportingRepresentative: this.reporting.get("sendToReportingRep")?.value ?? false,
      isUsingTaxRepresentative: this.reporting?.get("isUsingTaxRepresentative")?.value ?? false,
    };
    console.log(model);
    this.enrollment.registerHotelMotel(model).subscribe({
      next: (response) => {},
      error: (error) => {
        this.showNotification(
          "An error occurred while saving the information. Please try again later.",
          "Bed & Breakfast / Hotel / Motel",
          NotificationType.Error
        );
      },
      complete: () => {
        this.stepCompleted = true;
      },
    });
  }
  async selectPlace(place: any) {
    console.log(place);

    this.suggestions = [];
    const details = await this.googlePlacesService.getPlaceDetails(place.place_id);
    let selectedZipCode = this.googlePlacesService.extractLocationDetails(details);

    if (selectedZipCode.zip) {
      this.hotelForm.get("zipCode")?.setValue(selectedZipCode.zip, { emitEvent: false });
    }

    if (selectedZipCode.city) {
      this.hotelForm.get("city")?.setValue(selectedZipCode.city);
    }

    if (selectedZipCode.stateCode) {
      let stateId = this.states.find((e) => e.iso2 == selectedZipCode.stateCode)?.id ?? 0;
      console.log(stateId.toString());
      this.hotelForm.get("state")?.setValue(stateId.toString());
    }
    if (selectedZipCode.address) {
      this.hotelForm.get("ownerStreetAddress")?.setValue(selectedZipCode.address);
      this.hotelForm.get("ownerStreetAddress")?.updateValueAndValidity();
      this.hotelForm.get("ownerStreetAddress")?.markAsUntouched();
    }
  }
  // Cancel leaves the flow; canDeactivate asks "Leave registration?" when data was entered
  // (the old dialog opened without data and crashed, leaving a stuck overlay — QA 2026-10-05).
  openDialog(): void {
    this.router.navigate(["register/createAccount"]);
  }

  hasUnsavedChanges(): boolean {
    if (this.stepCompleted) {
      return false;
    }
    return [
      this.hotelForm,
      this.reporting,
      this.entityRentalPropertyLicense,
      this.rentalProperty,
      this.rentalPropertyLicense,
      this.agreements,
    ].some((form) => form?.dirty);
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasUnsavedChanges()) {
      return true;
    }
    const dialogRef = this.dialogRef.open(DialogConfirmComponent, {
      width: "479px",
      data: {
        title: "Leave registration?",
        message:
          "If you leave now you will start over and lose all the information you have entered.",
        confirmButtonText: "LEAVE & LOSE DATA",
        cancelButtonText: "STAY ON PAGE",
      },
    });
    return dialogRef.afterClosed().pipe(map((result) => result === true));
  }

  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = "You have unsaved registration data.";
    }
  }
}
